// This is Route component to build-up i18n
import { LOCAL_STORAGE } from "@constants/index";
import { PermissionContext } from "@contexts/PermissionContext";
import en from "@locales/en/translation.json";
import ko from "@locales/ko/translation.json";
import ja from "@locales/ja/translation.json";
import i18n from "i18next";
import { useContext, useEffect, useState } from "react";
import { initReactI18next } from "react-i18next";
import { Outlet } from "react-router-dom";
import { useFetchData } from "@hooks/useFetchData";
import queryKeys from "@configs/react-query/query-key";

function Root() {
  const [isI18nInitialized, setIsI18nInitialized] = useState(false);
  const { localeDefault } = useContext(PermissionContext);

  // Fetch default locale from API if needed (public API, no auth required)
  const savedLanguage = localStorage.getItem(LOCAL_STORAGE.LANGUAGE);
  const needsLocaleFromAPI = !savedLanguage;

  const {
    data: localeLanguageData,
    isPending: isLocalePending,
    isError: isLocaleError,
  } = useFetchData(
    [queryKeys.getLocaleLanguage],
    "locale_default",
    { 
      method: "GET",
      skipErrorToast: true, // Skip error toast for this public API call
    },
    {},
    {
      skipAuth: true, // Skip authentication check for public API
      enabled: needsLocaleFromAPI, // Only fetch if no LANGUAGE
      retry: 1, // Only retry once if fails
      staleTime: Infinity, // Cache forever since default locale doesn't change often
    }
  );

  useEffect(() => {
    // Wait for the public default-locale fetch before initializing i18n
    if (needsLocaleFromAPI && isLocalePending && !isLocaleError) {
      return;
    }

    let cancelled = false;

    const initI18n = async () => {
      let language = savedLanguage;

      if (!language) {
        language = localeLanguageData?.locale ?? localeDefault ?? "ko";
        // Persist so Header LanguageSelector and deep-linked pages keep the same default
        if (localeLanguageData?.locale) {
          localStorage.setItem(LOCAL_STORAGE.LANGUAGE, language);
        }
      }

      const initOptions = {
        resources: {
          en: { translation: en },
          EN: { translation: en },
          ko: { translation: ko },
          KO: { translation: ko },
          ja: { translation: ja },
          JA: { translation: ja },
        },
        lng: language,
        fallbackLng: "ko",
        interpolation: { escapeValue: false },
        pluralSeparator: '_',
        keySeparator: '.',
      };

      if (!i18n.isInitialized) {
        await i18n.use(initReactI18next).init(initOptions);
      } else if (i18n.language !== language) {
        await i18n.changeLanguage(language);
      }

      if (!cancelled) {
        setIsI18nInitialized(true);
      }
    };

    initI18n();

    return () => {
      cancelled = true;
    };
  }, [needsLocaleFromAPI, isLocalePending, isLocaleError, savedLanguage, localeLanguageData, localeDefault]);

  if (!isI18nInitialized) return <div>Loading...</div>;
  return <Outlet />;
}

export default Root;
